import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { X, Upload, FileText, CheckCircle, AlertTriangle, Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

const CSV_FIELDS = [
  { id: '', label: '— Ignore —' },
  { id: 'first_name', label: 'First Name' },
  { id: 'last_name', label: 'Last Name' },
  { id: 'name', label: 'Full Name' },
  { id: 'email', label: 'Email *' },
  { id: 'phone', label: 'Phone' },
  { id: 'title', label: 'Title' },
  { id: 'company', label: 'Company' },
  { id: 'state', label: 'State' },
  { id: 'city', label: 'City' },
  { id: 'country', label: 'Country' },
  { id: 'industry', label: 'Industry' },
  { id: 'linkedin_url', label: 'LinkedIn URL' },
  { id: 'website', label: 'Website' },
  { id: 'annual_revenue', label: 'Annual Revenue' },
  { id: 'employee_count', label: '# Employees' },
];

export const CsvImportModal = ({ onClose, onImported }) => {
  const [step, setStep] = useState(1); // 1: upload, 2: map, 3: result
  const [file, setFile] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [previewRows, setPreviewRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [assignToMe, setAssignToMe] = useState(true);
  const [source, setSource] = useState('Apollo.io CSV');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFile = async (f) => {
    if (!f) return;
    setError('');
    setFile(f);
    setLoading(true);
    try {
      const text = await f.text();
      setCsvText(text);
      const formData = new FormData();
      formData.append('file', f);
      const { data } = await ax.post('/api/admin/leads/csv-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setHeaders(data.headers);
      setMapping(data.suggested_mapping);
      setPreviewRows(data.preview_rows);
      setTotalRows(data.total_rows);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to parse CSV');
    }
    setLoading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleImport = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await ax.post('/api/admin/leads/csv-import', {
        csv_text: csvText,
        mapping,
        assign_to_me: assignToMe,
        source,
        stage: 'New Lead',
        skip_duplicates: true,
      });
      setResult(data);
      setStep(3);
      if (onImported) onImported();
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
    }
    setLoading(false);
  };

  const hasEmailMapped = Object.values(mapping).includes('email');

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
      data-testid="csv-import-modal"
    >
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 900, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: '1px solid #E5E7EB' }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.125rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
              Import Leads from CSV
            </h2>
            <p style={{ fontSize: '0.813rem', color: '#6B7280', margin: 0 }}>
              {step === 1 && 'Upload an Apollo.io export or any CSV with lead data'}
              {step === 2 && `Step 2: Map your columns • ${totalRows} rows detected`}
              {step === 3 && 'Import complete'}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }} data-testid="csv-modal-close">
            <X size={22} />
          </button>
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div style={{ padding: '1.5rem' }}>
            <label
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                border: '2px dashed #D1D5DB', borderRadius: 12, padding: '3rem', cursor: 'pointer',
                background: '#FAFBFC', transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--teal)'; e.currentTarget.style.background = '#F0FDFA'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#D1D5DB'; e.currentTarget.style.background = '#FAFBFC'; }}
              data-testid="csv-dropzone"
            >
              <input type="file" accept=".csv" onChange={(e) => handleFile(e.target.files[0])} style={{ display: 'none' }} data-testid="csv-file-input" />
              {loading ? (
                <Loader2 size={36} className="animate-spin" style={{ color: 'var(--teal)' }} />
              ) : (
                <>
                  <Upload size={36} style={{ color: 'var(--teal)', marginBottom: '0.75rem' }} />
                  <div style={{ fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>
                    Drop your CSV here, or click to browse
                  </div>
                  <div style={{ fontSize: '0.813rem', color: '#6B7280' }}>
                    Apollo.io exports work out of the box • Max 5MB
                  </div>
                </>
              )}
            </label>

            {error && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#FEE2E2', color: '#991B1B', borderRadius: 8, fontSize: '0.875rem' }}>
                {error}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Map columns */}
        {step === 2 && (
          <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', color: '#6B7280', fontSize: '0.875rem' }}>
              <FileText size={16} /> {file?.name} • {totalRows} rows
            </div>

            {/* Column mapping */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0.5rem', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>Your CSV Column</div>
              <div></div>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>Maps To</div>
              {headers.map((h) => (
                <React.Fragment key={h}>
                  <div style={{ padding: '0.5rem 0.75rem', background: '#F3F4F6', borderRadius: 6, fontSize: '0.875rem', fontWeight: 500, color: 'var(--navy)' }}>
                    {h}
                  </div>
                  <ArrowRight size={14} style={{ color: '#9CA3AF' }} />
                  <select
                    value={mapping[h] || ''}
                    onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                    data-testid={`csv-map-${h.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                    style={{ padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: '0.875rem', background: '#fff' }}
                  >
                    {CSV_FIELDS.map((f) => (
                      <option key={f.id} value={f.id}>{f.label}</option>
                    ))}
                  </select>
                </React.Fragment>
              ))}
            </div>

            {/* Options */}
            <div style={{ display: 'flex', gap: '1rem', padding: '1rem', background: '#F9FAFB', borderRadius: 8, marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                <input type="checkbox" checked={assignToMe} onChange={(e) => setAssignToMe(e.target.checked)} data-testid="csv-assign-me" />
                Assign all leads to me
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                <span>Source:</span>
                <input value={source} onChange={(e) => setSource(e.target.value)} style={{ padding: '0.25rem 0.5rem', border: '1px solid #D1D5DB', borderRadius: 4, fontSize: '0.813rem' }} />
              </div>
            </div>

            {!hasEmailMapped && (
              <div style={{ padding: '0.75rem', background: '#FEF3C7', color: '#92400E', borderRadius: 8, fontSize: '0.875rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertTriangle size={16} /> Email column is required for import
              </div>
            )}

            {error && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#FEE2E2', color: '#991B1B', borderRadius: 8, fontSize: '0.875rem' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <Button onClick={() => setStep(1)} className="btn-secondary" data-testid="csv-back-btn">Back</Button>
              <Button
                onClick={handleImport}
                disabled={!hasEmailMapped || loading}
                className="btn-primary"
                data-testid="csv-import-btn"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                <span style={{ marginLeft: 6 }}>Import {totalRows} leads</span>
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Result */}
        {step === 3 && result && (
          <div style={{ padding: '2rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <CheckCircle size={48} style={{ color: '#10B981', margin: '0 auto 0.75rem' }} />
              <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
                Import complete
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{ textAlign: 'center', padding: '1rem', background: '#D1FAE5', borderRadius: 8 }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#065F46' }}>{result.imported}</div>
                <div style={{ fontSize: '0.75rem', color: '#065F46' }}>Imported</div>
              </div>
              <div style={{ textAlign: 'center', padding: '1rem', background: '#FEF3C7', borderRadius: 8 }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#92400E' }}>{result.skipped_duplicates}</div>
                <div style={{ fontSize: '0.75rem', color: '#92400E' }}>Duplicates skipped</div>
              </div>
              <div style={{ textAlign: 'center', padding: '1rem', background: '#FEE2E2', borderRadius: 8 }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#991B1B' }}>{result.total_errors}</div>
                <div style={{ fontSize: '0.75rem', color: '#991B1B' }}>Errors</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem', maxHeight: 150, overflow: 'auto' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Error details:</div>
                {result.errors.slice(0, 20).map((e, i) => (
                  <div key={i} style={{ fontSize: '0.75rem', color: '#991B1B' }}>Row {e.row}: {e.error}</div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button onClick={onClose} className="btn-primary" data-testid="csv-done-btn">Done</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
